import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { IQuestion } from "../interfaces/Question";

interface IFetchQuestion {
    programId: string;
}

interface IStoreQuestions {
    programId: string
    questions: IQuestion[]
}

interface IDeleteQuestion {
    questionId: string
}

interface ProgramQuestionsState {
    loading: boolean;
    error: string | null;
    programQuestions: IQuestion[] | [];
}

const initialState: ProgramQuestionsState = {
    loading: false,
    error: null,
    programQuestions: [],
};

/**
 * Axios request to fetch questions for a given programId
 */
export const fetchQuestions = createAsyncThunk('questions/fetchQuestions',
    async ({ programId }: IFetchQuestion, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios(`${BASE_URL}/api/v1/programs/${programId}/questions`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            return responseData?.data?.questions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * AXIOS request for store questions
 */
export const storeQuestions = createAsyncThunk('questions/storeQuestions',
    async ({ programId, questions }: IStoreQuestions, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios.post(`${BASE_URL}/api/v1/questions`, {
                programId,
                questions
            }, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            thunkAPI.dispatch(fetchQuestions({ programId: programId as string }))
            return responseData?.data?.questions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

/**
 * Axios API request to delete a question
 */
export const deleteQuestion = createAsyncThunk('questions/storeQuestions',
    async ({ questionId }: IDeleteQuestion, thunkAPI) => {
        try {
            const accessToken = JSON.parse(localStorage.getItem('loginData') as string)?.accessToken;

            if (!accessToken) {
                console.error('Access token not found in the response.');
                return {}; // or any default value if needed
            }
            const response = await axios.delete(`${BASE_URL}/api/v1/questions/${questionId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    // Add other headers if needed
                },
            });
            const responseData = response?.data
            if (response.status !== 200 && response.statusText !== 'OK') {
                throw new Error(`Request failed with status ${response?.data?.message}`);
            }
            return responseData?.data?.questions || [];
        } catch (error: any) {
            console.error('Error:', error.message);
            return thunkAPI.rejectWithValue(`Fetch failed: ${error.message}`);
        }
    }
);

const programQuestionsSlice = createSlice({
    name: "programQuestions",
    initialState,
    reducers: {
        resetState: (state) => initialState
    },
    extraReducers: (builder) => {
        builder.addCase(fetchQuestions.pending, (state) => {
            state.loading = true;
        })
            .addCase(fetchQuestions.fulfilled, (state, action) => {
                state.loading = false;
                state.programQuestions = action.payload;
            })
            .addCase(fetchQuestions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'An error occurred';
            });
    },
});

export const { resetState } = programQuestionsSlice.actions;
export default programQuestionsSlice.reducer;